import type { ComponentProps } from 'react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ReclamationAccessPolicy } from 'graphql-ts-types';
import { display } from 'styled-system';

import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import { Heading } from 'common/components/Text';
import { ReclamationReason } from 'common/enums/reasons';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useAdditionalInfo } from '../../hooks/additional-info';
import { usePictureGuideButtonClickHandler } from '../../hooks/additional-info/picture-guide';
import { usePictureGuideState } from '../../hooks/additional-info/pictures';
import type { BaseReclamationStepProps } from '../../types/steps';
import type { AdditionalInfoTrackingEventData } from '../../utils/tracking';
import AdditionalFeedback from './AdditionalFeedback';
import PartNumberAddition from './PartNumberAddition';
import ReclamationStepImageUpload from './ReclamationStepImageUpload';
import ReclamationTakePictureGuide from './ReclamationTakePictureGuide';
import VendorOrderId from './VendorOrderId';

export interface ReclamationStepAdditionalInfoProps extends BaseReclamationStepProps {
  trackingEventData: AdditionalInfoTrackingEventData;
}

const Section = styled.section<any>`
  ${display};
`;

const UnstyledStyledHeading = styled(Heading)``;

const StyledHeading = (props: ComponentProps<typeof UnstyledStyledHeading>) => (
  <UnstyledStyledHeading
    color="sys.neutral.text.default"
    fontSize="fs_16"
    lineHeight="lh_20"
    mt="sp_4"
    mb="sp_16"
    {...props}
  />
);

const UnstyledStyledText = styled(StyledHeading)``;

const StyledText = (props: ComponentProps<typeof UnstyledStyledText>) => (
  <UnstyledStyledText fontWeight="normal" {...props} />
);

// eslint-disable-next-line max-lines-per-function
const ReclamationStepAdditionalInfo = ({
  type,
  reclamationId,
  itemId,
  alert,
  hasInvalidField,
  trackingEventData,
  setOnSubmit,
  setHeaderProps,
  resetHeaderProps,
  setIsNextDisabled,
  setIsNextHidden,
  setIsFooterVisible,
}: ReclamationStepAdditionalInfoProps) => {
  const { r, t } = useLocalization();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pictureCount, setPictureCount] = useState(0);
  const [selectedReason, setSelectedReason] = useState('');
  const [isPictureGuideOpen, setPictureGuideOpen, setPictureGuideClosed] =
    usePictureGuideState(setIsNextHidden);
  const {
    isImageAllowed,
    partNumbersAllowed,
    partNumbers,
    commentPolicy,
    comment,
    isFetching,
    internalAlert,
    assemblyInstruction,
    partNumberPolicy,
    vendorOrderId,
    vendorOrderIdPolicy,
    setPartNumbers,
    setComment,
    setVendorOrderId,
  } = useAdditionalInfo({
    reclamationId,
    itemId,
    alert,
    isPictureGuideOpen,
    pictureCount,
    trackingEventData,
    setIsNextDisabled,
    setIsFooterVisible,
    resetHeaderProps,
    setOnSubmit,
    setSelectedReason,
  });
  const handleGuideButtonClick = usePictureGuideButtonClickHandler({
    setPictureGuideOpen,
    setIsFooterVisible,
  });
  const triggerAddPictureInput = () => fileInputRef.current?.click();
  const infoSubtitle =
    selectedReason === ReclamationReason.EDGES_DAMAGED
      ? t('h24_reclamation_information_subtitle_package_damaged')
      : t('h24_reclamation_information_subtitle');

  useEffect(() => {
    if (!isPictureGuideOpen) {
      setHeaderProps({
        title: t('h24_reclamation_information_title'),
      });
    }
  }, [isPictureGuideOpen]);

  return (
    <Fragment>
      <Section
        data-testid={`reclamation-step-${type}-${itemId}`}
        {...(isPictureGuideOpen ? { display: 'none' } : {})}
      >
        <ReclamationAlert {...internalAlert} />
        <StyledText data-testid="information-subtitle">{infoSubtitle}</StyledText>
        {isImageAllowed && (
          <ReclamationStepImageUpload
            reclamationId={reclamationId}
            itemId={itemId}
            fileInputRef={fileInputRef}
            trackingEventData={trackingEventData}
            onGuideButtonClick={handleGuideButtonClick}
            onPictureCountChange={setPictureCount}
          />
        )}
        {vendorOrderIdPolicy !== ReclamationAccessPolicy.Disallowed && (
          <VendorOrderId
            accessPolicy={vendorOrderIdPolicy}
            onChange={setVendorOrderId}
            value={vendorOrderId}
            areInputsDisabled={isFetching}
            shouldValidate={Boolean(hasInvalidField)}
          />
        )}
        {partNumbersAllowed && (
          <PartNumberAddition
            values={partNumbers}
            areInputsDisabled={isFetching}
            assemblyInstruction={assemblyInstruction}
            shouldValidate={hasInvalidField}
            partNumberPolicy={partNumberPolicy}
            trackingEventData={trackingEventData}
            onChange={setPartNumbers}
          />
        )}
        {commentPolicy !== ReclamationAccessPolicy.Disallowed && (
          <AdditionalFeedback
            value={comment}
            isCommentDisabled={isFetching}
            shouldValidate={hasInvalidField}
            commentPolicy={commentPolicy}
            onChange={setComment}
          />
        )}
      </Section>
      {isPictureGuideOpen && (
        <ReclamationTakePictureGuide
          triggerAddPictureInput={triggerAddPictureInput}
          closeGuide={setPictureGuideClosed}
          setHeaderProps={setHeaderProps}
          backButtonLabel={r.t('h24_go_back_to_step', {
            step: t('h24_reclamation_information_title'),
          })}
        />
      )}
    </Fragment>
  );
};

export default ReclamationStepAdditionalInfo;
